import { APP_CONFIG_ENDPOINT } from "../config/urls";
import { fetcher } from "../helpers/fetcher";
import {
  IS_ADS_ENABLED,
  IS_JC_ENABLED,
  IS_ATOZ_ENABLED,
  HIDE_ATOZ_TASK,
  WORK_AUTHORIZATION_DOC_CONFIGS,
  TASK_FEATURE_FLAG_CONFIG,
  CUSTOMER_SERVICE,
  REQUISITION_CATEGORY,
  TASK_DEFINITION_CONFIG,
  NON_CUSTOMER_SERVICE,
  RIGHT_TO_WORK_TASK_TITLE,
  RIGHT_TO_WORK_TASK_DEFINITION_ID,
  LAUNCHED_COUNTRY,
  IS_GLOBAL_ID_ENABLED,
} from "../config/appConfigConstants";
import { getJobScheduleDetails, getSalesforceJobDetails, JobDetailsModel } from "./application-data";
import { save, load } from "./local-storage";
import { WorkAuthorizationDocumentModel } from "../models/work-authorization-document-model";
import { AttachmentType } from "../routes/tasks/document/models";
import { isSalesforceApplication } from "../helpers/url-params";
import { getRegionFromCookie } from "./locale-helper";

export interface CandidateData {
  candidateId: string;
  sfCandidateId: string;
}

export const getConfigurations = () => {
  if (process.env.NODE_ENV === "development") {
    if (process.env.REACT_APP_USE_MOCK_REQUESTS === "true") {
      console.warn("getConfigurations", "mocked request");
      return new Promise((resolve) => {
        setTimeout(() => {
          resolve({
            configurations: {
              configurationsArray: [
                {
                  configurationName: "isAdsEnabled",
                  defaultValue: true,
                },
                {
                  configurationName: "isJobContainerEnabled",
                  defaultValue: true,
                },
                {
                  configurationName: "isAtoZEnabled",
                  defaultValue: true,
                  siteFilters: ["SITE-BUF5", "SITE-BWI5", "SITE-OKC5", "SITE-KCPD", "SITE-DXC5", "SITE-MIA5"],
                },
              ],
            },
            isCached: true,
          });
        }, 1000);
      });
    }
  }

  return fetcher(APP_CONFIG_ENDPOINT);
};

export async function processConfigurations(appConfig: any, applicationId: string) {
  if (!appConfig || !appConfig.configurations || !appConfig.configurations.configurationsArray) {
    //no valid AppConfig or no valid format found for AppConfig
    return;
  }
  const scheduleDetails = !isSalesforceApplication(applicationId)
    ? await getJobScheduleDetails(applicationId)
    : await getSalesforceJobDetails(applicationId);

  if (scheduleDetails.requisitionCategory) {
    save(REQUISITION_CATEGORY + applicationId, scheduleDetails.requisitionCategory);
  }

  for (const config of appConfig.configurations.configurationsArray) {
    const configName = config.configurationName;
    if (!config.configurationName) {
      continue;
    }
    const configType = config.configurationType;
    //saves into local storage
    if (configName == IS_ATOZ_ENABLED) {
      let hideAtoZTask = true;
      if (config.defaultValue) {
        if (config.siteFilters.length == 0) {
          hideAtoZTask = false;
        } else if (scheduleDetails.siteId) {
          if (config.siteFilters.includes(scheduleDetails.siteId)) {
            hideAtoZTask = false;
          }
        }
      }
      save(HIDE_ATOZ_TASK + applicationId, hideAtoZTask);
    } else if (configName == WORK_AUTHORIZATION_DOC_CONFIGS) {
      save(WORK_AUTHORIZATION_DOC_CONFIGS, config.defaultValue);
    }

    if (configType == TASK_FEATURE_FLAG_CONFIG) {
      await setFeatureFlagForTaskList(applicationId, config, scheduleDetails);
    }

    if (configType == TASK_DEFINITION_CONFIG) {
      save(config.taskId + applicationId, config.definitions);
    }

    if (configName == IS_GLOBAL_ID_ENABLED) {
      save(IS_GLOBAL_ID_ENABLED, config.defaultValue);
    }
  }
}

export function isADSEnabled() {
  const configValue = localStorage.getItem(IS_ADS_ENABLED);
  // handleCriticalConfigError(IS_ADS_ENABLED, configValue);
  //turn on by default
  return configValue == "false" ? false : true;
}

export function isJobContainerEnabled() {
  const configValue = localStorage.getItem(IS_JC_ENABLED);
  // handleCriticalConfigError(IS_JC_ENABLED, configValue);
  return configValue == "false" ? false : true;
}

export async function setFeatureFlagForTaskList(applicationId: string, config: any, jobDetails: JobDetailsModel) {
  if (jobDetails.error) {
    save(config.taskId + applicationId, false);
    return;
  }

  if (!config.overrideConditions) {
    save(config.taskId + applicationId, config.defaultValue);
    return;
  }

  const countryGroup = jobDetails.countryGroup;
  const siteId = jobDetails.siteId;
  const businessLine = jobDetails.businessLine;
  const jobId = jobDetails.jobId;
  const requisitionCategory = jobDetails.requisitionCategory;
  let overrideValue = undefined;
  for (const condition of config.overrideConditions) {
    if (!isConfigMatched(condition.countryList, countryGroup, "countryList")) {
      continue;
    }
    if (!isConfigMatched(condition.siteList, siteId, "siteId")) {
      continue;
    }
    if (!isConfigMatched(condition.businessLineList, businessLine, "businessLine")) {
      continue;
    }
    if (!isConfigMatched(condition.jobIdList, jobId, "jobId")) {
      continue;
    }
    if (!isConfigMatched(condition.requisitionCategoryList, requisitionCategory, "requisitionCategory")) {
      continue;
    }

    overrideValue = condition.overrideValue;
    break; //found a matched entry in appConfig to override default config
  }

  const taskEnabled = overrideValue != undefined ? overrideValue : config.defaultValue;
  save(config.taskId + applicationId, taskEnabled);
}

function isConfigMatched(configList: string[], key: string | undefined, configListName?: string) {
  if (!configList || configList.length == 0) {
    return true;
  }
  if (!key) {
    console.error("Failed to load config when condition is null for config list", configListName);
    return false;
  }
  return configList.includes(key);
}

export async function getWorkAuthorizationDocumentList(
  applicationId: string
): Promise<WorkAuthorizationDocumentModel[]> {
  const jobScheduleDetails = !isSalesforceApplication(applicationId)
    ? await getJobScheduleDetails(applicationId)
    : await getSalesforceJobDetails(applicationId);
  const countryGroup = jobScheduleDetails.countryGroup;
  const siteId = jobScheduleDetails.siteId;
  const businessLine = jobScheduleDetails.businessLine;
  const documentConfigs = load(WORK_AUTHORIZATION_DOC_CONFIGS);

  if (!Array.isArray(documentConfigs)) {
    return [];
  }
  const workAuthorizationDocumentModels = [];

  for (const documentConfig of documentConfigs) {
    const enabledConditions = documentConfig.enableConditions;
    if (!enabledConditions) {
      continue;
    }

    let docEnabled = true;
    //Enable condition for country code
    if (!isConfigMatched(enabledConditions.countryAllowlist, countryGroup)) {
      docEnabled = false;
    }
    //Enable condition for site id
    if (!isConfigMatched(enabledConditions.siteAllowlist, siteId)) {
      docEnabled = false;
    }
    //Enable condition for business line
    if (!isConfigMatched(enabledConditions.businessLineAllowlist, businessLine)) {
      docEnabled = false;
    }

    if (docEnabled) {
      const document = {
        documentName: documentConfig.documentName,
        documentType: documentConfig.documentType as AttachmentType,
        documentDescription: documentConfig.documentDescription,
        documentCategory: documentConfig.documentCategory, //used to group documents
        requiredPages: documentConfig.requiredPages, //e.g. Front, Back page
        requiredIdInput: documentConfig.requiredIdInput, //e.g. curpId input required on UI
      } as WorkAuthorizationDocumentModel;
      workAuthorizationDocumentModels.push(document);
    }
  }
  return workAuthorizationDocumentModels;
}

export function isTaskEnabledByAppConfig(taskId: string, applicationId: string) {
  return load(taskId + applicationId) as boolean;
}

export function getTaskDefinitionsFromConfig(taskId: string, applicationId?: string) {
  if (!applicationId) {
    return undefined;
  }
  try {
    // TODO: will be removed later when integration with Deloittle is ready.
    const country = getRegionFromCookie() as LAUNCHED_COUNTRY;
    if (
      taskId === RIGHT_TO_WORK_TASK_DEFINITION_ID &&
      country !== LAUNCHED_COUNTRY.US &&
      isCustomerService(applicationId)
    ) {
      const task = load(taskId + applicationId);
      return { ...task, isIntegratedWithCaaS: false };
    }

    return load(taskId + applicationId);
  } catch (e) {
    console.info(`No task definition for taskId: ${taskId} and applicationId: ${applicationId}`);
    return undefined;
  }
}

export function isCustomerService(applicationId: string) {
  return load(REQUISITION_CATEGORY + applicationId) == CUSTOMER_SERVICE;
}

export function getJobType(applicationId: string) {
  if (isCustomerService(applicationId)) {
    return CUSTOMER_SERVICE;
  }

  return NON_CUSTOMER_SERVICE;
}

export function getRightToWorkOrI9TaskType(taskTitle: string, applicationId: string) {
  return getTaskDefinitionsFromConfig(taskTitle, applicationId)?.taskTitle == RIGHT_TO_WORK_TASK_TITLE;
}

export function isGlobalIdEnabledByAppConfig() {
  return load(IS_GLOBAL_ID_ENABLED);
}
